import {Group} from "@visx/group";
import {ParentSize} from "@visx/responsive";
import {Pie} from "@visx/shape";
import {useEffect, useState} from "react";
import {instanceMob} from "../../../../../utils/utils.js";
import ConfirmDeletionModal from "../../modal-delete.js";
import GraphicsLegend from "./graphics-legend.tsx";
import {useTooltip, useTooltipInPortal} from "@visx/tooltip";
import "./styles.css";


interface PieData {
    label: string;
    value: number;
    fill?: string;
}

const colors = [
    "#2A4A6D",
    "#3177BE",
    "#70B6C1",
    "#D8E3FC",
    "#B9CDF7",
    "#3A8DDE",
    "#394F65",
    "#006D86",
    "#808FF3",
    "#8FCDF7",
    "#91BEEB",
    "#6A8FB5",
    "#D9D9D9",
];

const dateOptions = [
    {id: 1, name: "Últimos 7 días", value: "7d"},
    {id: 2, name: "Último mes", value: "1m"},
    {id: 3, name: "Últimos 3 meses", value: "3m"},
    {id: 4, name: "Últimos 6 meses", value: "6m"},
    {id: 5, name: "Último año", value: "1y"},
    {id: 6, name: "Todo", value: "all"},
];

type PieType = "plan" | "gender";

function shuffle<T>(array: T[]): T[] {
    const copy = [...array];
    const result: T[] = [];

    while (copy.length > 0) {
        const randomIndex = Math.floor(Math.random() * copy.length);
        const [item] = copy.splice(randomIndex, 1);
        result.push(item);
    }

    return result;
}

export default function PieChart({type = "plan" as PieType}) {
    const [data, setData] = useState<PieData[] | null>(null);
    const [modalError, setModalError] = useState<JSX.Element | null>(null);
    const labels = data?.map((d) => `${d.label} (${d.value}${type === "plan" ? "%" : ""})`) || [];
    const [loading, setLoading] = useState(false);
    const {containerRef, TooltipInPortal} = useTooltipInPortal({
        scroll: true,
        detectBounds: true,
    });
    const {
        tooltipData,
        tooltipLeft,
        tooltipTop,
        tooltipOpen,
        showTooltip,
        hideTooltip,
    } = useTooltip<PieData>();
    const [dateRange, setDateRange] = useState("7d");
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const [locations, setLocations] = useState<any[]>([]);
    const [locationSelected, setLocationSelected] = useState<string>("-1");
    const [isLocationOpen, setIsLocationOpen] = useState(false);
    const [shuffledColors, setShuffledColors] = useState<string[]>([]);

    const getLocations = async () => {
        try {
            const res = await instanceMob.get("admin/location/list");
            setLocations(res.data || []);
        } catch (err) {
            console.error(err);
        }
    };

    async function getData() {
        try {
            setLoading(true);
            let url = "";

            if (type === "plan") {
                url = `admin/metrics/plan?range=${dateRange}`;
                if (locationSelected !== "-1") url += `&location=${locationSelected}`;
            } else if (type === "gender") {
                url = `admin/metrics/customer/gender?range=${dateRange}`;
                if (locationSelected !== "-1") url += `&location=${locationSelected}`;
            }

            const response = await instanceMob.get(url);
            const apiData = response.data;

            const rawArray = Array.isArray(apiData) ? apiData : Array.isArray(apiData?.data) ? apiData.data : [];

            let data_new: PieData[] = [];

            if (type === "plan") {
                data_new = rawArray.map((ele: any) => ({
                    label: ele.plan_name || ele.name || "Sin plan",
                    value: Number(ele.percentage ?? ele.value ?? 0),
                }));
            } else if (type === "gender") {
                data_new = rawArray.map((ele: any) => ({
                    label: ele.gender_display || "Desconocido",
                    value: Number(ele.percentage ?? 0),
                }));
            }


            setData(data_new);
        } catch (err: any) {
            const msg_error = err?.response?.data;
            if (msg_error) {
                setModalError(
                    <ConfirmDeletionModal onCancel={() => setModalError(null)} onConfirm={() => setModalError(null)}
                                          loader={false}>
                        {msg_error?.error || "Algo sucedió, intenta de nuevo"}
                    </ConfirmDeletionModal>
                );
            } else {
                console.error(err);
            }
            setData([]);
        } finally {
            setLoading(false);
        }
    }

    useEffect(() => {
        if (data && data.length > 0) {
            setShuffledColors(shuffle(colors));
        }
    }, [data]);

    useEffect(() => {
        getLocations();
    }, []);

    useEffect(() => {
        getData();
    }, [dateRange, type, locationSelected]);

    return (
        <>
            <div className="d-flex justify-content-end gap-2 align-items-center w-100 px-3">
                <div className="dropdown text-center position-relative">
                    <button
                        className="btn dropdown-toggle card-select d-flex justify-content-between align-items-center"
                        type="button"
                        id="locationDropdown"
                        aria-expanded={isLocationOpen}
                        onClick={() => setIsLocationOpen(!isLocationOpen)}
                    >
                        <span>
                            {locationSelected === "-1"
                                ? "Todas las sedes"
                                : locations.find((loc) => String(loc.id) === locationSelected)?.name ||
                                "Selecciona"}
                        </span>
                        <span
                            dangerouslySetInnerHTML={{
                                __html: `<svg width="15" height="9" viewBox="0 0 15 9" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M7.26912 8.89571C6.91484 8.89571 6.56051 8.76057 6.29014 8.49022L0.925015 3.12509C0.38437 2.58444 0.38437 1.70785 0.925015 1.16719C1.46564 0.62656 2.34226 0.62656 2.88289 1.16719L7.26912 5.55347L11.6553 1.16719C12.196 0.62656 13.0726 0.62656 13.6132 1.16719C14.1539 1.70785 14.1539 2.58444 13.6132 3.12509L8.2481 8.49022C7.97775 8.76057 7.6234 8.89571 7.26912 8.89571Z" fill="#9DACBD"/></svg>`,
                            }}
                        />
                    </button>
                    <ul
                        className={`dropdown-menu w-100 card-select-menu p-2 ${
                            isLocationOpen ? "show" : ""
                        }`}
                        aria-labelledby="locationDropdown"
                    >
                        <li
                            key="-1"
                            className={`card-select-label dropdown-item ${
                                locationSelected === "-1" ? "active" : ""
                            }`}
                            onClick={() => {
                                setLocationSelected("-1");
                                setIsLocationOpen(false);
                            }}
                            style={{ cursor: "pointer" }}
                        >
                            Todas las sedes
                        </li>
                        {locations.map((loc) => (
                            <li
                                key={loc.id}
                                className={`card-select-label dropdown-item ${
                                    locationSelected === String(loc.id) ? "active" : ""
                                }`}
                                onClick={() => {
                                    setLocationSelected(String(loc.id));
                                    setIsLocationOpen(false);
                                }}
                                style={{ cursor: "pointer" }}
                            >
                                {loc.name}
                            </li>
                        ))}
                    </ul>
                </div>
                <div className="dropdown text-center position-relative">
                    <button
                        className="btn dropdown-toggle card-select d-flex justify-content-between align-items-center"
                        type="button"
                        id="dateDropdown"
                        data-bs-toggle="dropdown"
                        aria-expanded={isDropdownOpen}
                        onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                    >
                        <span>{dateOptions.find((d) => d.value === dateRange)?.name || "Selecciona"}</span>
                        <span
                            dangerouslySetInnerHTML={{
                                __html: `<svg width="15" height="9" viewBox="0 0 15 9" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M7.26912 8.89571C6.91484 8.89571 6.56051 8.76057 6.29014 8.49022L0.925015 3.12509C0.38437 2.58444 0.38437 1.70785 0.925015 1.16719C1.46564 0.62656 2.34226 0.62656 2.88289 1.16719L7.26912 5.55347L11.6553 1.16719C12.196 0.62656 13.0726 0.62656 13.6132 1.16719C14.1539 1.70785 14.1539 2.58444 13.6132 3.12509L8.2481 8.49022C7.97775 8.76057 7.6234 8.89571 7.26912 8.89571Z" fill="#9DACBD"/></svg>`,
                            }}
                        />
                    </button>
                    <ul
                        className={`dropdown-menu w-100 card-select-menu p-2 ${isDropdownOpen ? "show" : ""}`}
                        aria-labelledby="dateDropdown"
                    >
                        {dateOptions.map((item) => (
                            <li
                                key={item.id}
                                className={`card-select-label dropdown-item ${dateRange === item.value ? "active" : ""}`}
                                onClick={() => {
                                    setDateRange(item.value);
                                    setIsDropdownOpen(false);
                                }}
                                style={{cursor: "pointer"}}
                            >
                                {item.name}
                            </li>
                        ))}
                    </ul>
                </div>
            </div>
            <div className="d-flex gap-3 justify-content-center p-3">
                {modalError}
                {loading ? (
                    <div className="d-flex justify-content-center align-items-center"
                         style={{width: "100%", height: "100%"}}>
                        <div className="spinner-border" role="status">
                            <span className="visually-hidden">Loading...</span>
                        </div>
                    </div>
                ) : data && data.length > 0 ? (
                    <div style={{width: "100%", height: "400px", minWidth: "400px"}}>
                        <ParentSize>
                            {({width, height}) => {
                                const radius = Math.min(width, height) / 2;
                                return (
                                    <svg ref={containerRef} width={width} height={height}>
                                        <Group top={height / 2} left={width / 2}>
                                            <Pie
                                                data={data}
                                                pieValue={(d: any) => d.value}
                                                outerRadius={radius}
                                                innerRadius={1}
                                                cornerRadius={5}
                                                padAngle={0.02}
                                            >
                                                {(pie) =>
                                                    pie.arcs.map((arc: any, index: any) => {
                                                        const arcPath = pie.path(arc) || "";
                                                        const fill = shuffledColors[index % shuffledColors.length];
                                                        const [labelX, labelY] = pie.path.centroid(arc);

                                                        return (
                                                            <g
                                                                key={`arc-${arc.data.label}`}
                                                                onMouseMove={(event) => {
                                                                    showTooltip({
                                                                        tooltipLeft: event.nativeEvent.offsetX,
                                                                        tooltipTop: event.nativeEvent.offsetY,
                                                                        tooltipData: {...arc.data, fill},
                                                                    });
                                                                }}
                                                                onMouseLeave={hideTooltip}
                                                            >
                                                                <path d={arcPath} fill={fill} stroke="#fff"
                                                                      strokeWidth={2}/>

                                                                <text
                                                                    x={labelX}
                                                                    y={labelY}
                                                                    dy=".33em"
                                                                    textAnchor="middle"
                                                                    style={{
                                                                        fontFamily: "Work Sans",
                                                                        fontWeight: 700,
                                                                        fontSize: "13px",
                                                                        fill: "#FFFFFF",
                                                                    }}
                                                                >
                                                                    {`${arc.data.value}${type === "plan" ? "%" : ""}`}
                                                                </text>
                                                            </g>
                                                        );
                                                    })
                                                }
                                            </Pie>
                                        </Group>
                                    </svg>
                                );
                            }}
                        </ParentSize>

                        {tooltipOpen && tooltipData && (
                            <TooltipInPortal top={tooltipTop} left={tooltipLeft}>
                                <div style={{textAlign: "center"}}>
                                    <b>{tooltipData?.label || ""}</b>
                                    <br/>
                                    <div className="d-flex align-items-center mt-1">
                                        <div style={{
                                            width: "12px",
                                            height: "12px",
                                            backgroundColor: tooltipData?.fill,
                                            borderRadius: "2px"
                                        }}/>
                                        <span className="ms-2">{tooltipData?.value}{type === "plan" ? "%" : ""}</span>
                                    </div>
                                </div>
                            </TooltipInPortal>
                        )}
                    </div>
                ) : (
                    <div className="d-flex align-items-center flex-column justify-content-center w-100 h-100">
                        <img alt="logo_alta_black_2" style={{padding: "0% 20%"}}
                             src={process.env.PUBLIC_URL + "/assets/img/logo_small_elevva_metrics.png"}/>
                        <span>No hay datos</span>
                    </div>
                )}

                <div className="divider-line-data"></div>

                <div className="d-flex flex-column align-items-start">
                    <div className="mb-3">
                        <div className="mt-2">
                            <span
                                className="graphics-legends-title">{type === "plan" ? "Top más consumidos" : "Distribución por sexo"}</span>
                        </div>
                    </div>
                    <div className="graphics-legends-label">
                        {labels.length > 0 && <GraphicsLegend labels={labels} colors={shuffledColors.slice(0, labels.length)}/>}
                    </div>
                </div>
            </div>
        </>

    );
}
